import React, { useEffect, useMemo, useState } from 'react';
import { authService } from '../services/authService';

const availableFeatureLabels = {
  custom_domains: 'Custom domains',
  advanced_analytics: 'Advanced analytics',
  forms: 'Forms',
  storefront: 'Storefront',
  r2_uploads: 'R2 uploads',
  priority_support: 'Priority support',
};

const formatProfileLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const SignInIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <path d="m10 17 5-5-5-5" />
    <path d="M15 12H3" />
  </svg>
);

function AuthModal({ mode, onClose, onModeChange }) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSignUp = mode === 'signUp';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (isSignUp) {
        await authService.registerWithEmail(email, password, displayName.trim());
      } else {
        await authService.signInWithEmail(email, password);
      }
      onClose();
    } catch (authError) {
      setError(authError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      await authService.signInWithGoogle();
      onClose();
    } catch (authError) {
      setError(authError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby="auth-modal-title"
        aria-modal="true"
        className="modal-panel"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-950" id="auth-modal-title">
              {isSignUp ? 'Create account' : 'Sign in'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {isSignUp ? 'Create a Sharabl account.' : 'Access your Sharabl workspace.'}
            </p>
          </div>
          <button aria-label="Close authentication modal" className="icon-button" onClick={onClose} type="button">
            x
          </button>
        </div>

        <form className="px-5 py-5" onSubmit={handleSubmit}>
          {isSignUp && (
            <>
              <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="auth-display-name">
                Name
              </label>
              <input
                autoComplete="name"
                className="field"
                id="auth-display-name"
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Your name"
                type="text"
                value={displayName}
              />
            </>
          )}

          <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700" htmlFor="auth-email">
            Email
          </label>
          <input
            autoComplete="email"
            className="field"
            id="auth-email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            required
            type="email"
            value={email}
          />

          <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700" htmlFor="auth-password">
            Password
          </label>
          <input
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            className="field"
            id="auth-password"
            minLength={6}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            required
            type="password"
            value={password}
          />

          {error && <p className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button className="nav-button active justify-center" disabled={isSubmitting} type="submit">
              {isSubmitting ? 'Working...' : isSignUp ? 'Sign up' : 'Sign in'}
            </button>
            <button className="nav-button justify-center" disabled={isSubmitting} onClick={handleGoogleSignIn} type="button">
              Continue with Google
            </button>
          </div>
        </form>

        <div className="border-t border-slate-200 px-5 py-4 text-sm text-slate-600">
          {isSignUp ? 'Already have an account?' : 'Need an account?'}
          <button
            className="ml-2 font-semibold text-slate-950 underline-offset-4 hover:underline"
            onClick={() => {
              setError('');
              onModeChange(isSignUp ? 'signIn' : 'signUp');
            }}
            type="button"
          >
            {isSignUp ? 'Sign in' : 'Sign up'}
          </button>
        </div>
      </section>
    </div>
  );
}

function ProfileModal({ user, onClose }) {
  const profile = user.profile || {};
  const [profileDraft, setProfileDraft] = useState({
    displayName: user.displayName || profile.displayName || '',
    photoURL: user.photoURL || profile.photoURL || '',
    mobileNumber: profile.mobileNumber || '',
    jobTitle: profile.jobTitle || '',
    bio: profile.bio || '',
    location: profile.location || '',
    website: profile.website || '',
    organisationName: profile.organisationName || '',
    organisationRole: profile.organisationRole || '',
    organisationWebsite: profile.organisationWebsite || '',
    organisationSize: profile.organisationSize || '',
    organisationAddress: profile.organisationAddress || '',
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const featureKeys = Array.isArray(profile.featureKeys) ? profile.featureKeys : [];
  const subscriptionPlan = profile.subscriptionPlan || 'free';
  const subscriptionStatus = profile.subscriptionStatus || 'trialing';

  const updateProfileDraft = (field, value) => {
    setProfileDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      await authService.updateCurrentProfile(Object.fromEntries(
        Object.entries(profileDraft).map(([key, value]) => [key, value.trim()]),
      ));
      onClose();
    } catch (profileError) {
      setError(profileError.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby="profile-modal-title"
        aria-modal="true"
        className="modal-panel profile-modal-panel"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-950" id="profile-modal-title">
              Profile
            </h2>
            <p className="mt-1 text-sm text-slate-500">{user.email}</p>
          </div>
          <button aria-label="Close profile modal" className="icon-button" onClick={onClose} type="button">
            x
          </button>
        </div>

        <form className="grid gap-5 px-5 py-5" onSubmit={handleSave}>
          <div className="mb-5 flex items-center gap-3">
            <UserAvatar user={{ ...user, displayName: profileDraft.displayName, photoURL: profileDraft.photoURL }} />
            <div>
              <p className="text-sm font-semibold text-slate-950">{profileDraft.displayName || user.email}</p>
              <p className="text-xs text-slate-500">Profile image preview</p>
            </div>
          </div>

          <section className="profile-form-section">
            <div>
              <p className="label-caps">Personal details</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-display-name">
                    Name
                  </label>
                  <input
                    autoComplete="name"
                    className="field"
                    id="profile-display-name"
                    onChange={(event) => updateProfileDraft('displayName', event.target.value)}
                    placeholder="Your name"
                    type="text"
                    value={profileDraft.displayName}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-mobile-number">
                    Mobile number
                  </label>
                  <input
                    autoComplete="tel"
                    className="field"
                    id="profile-mobile-number"
                    onChange={(event) => updateProfileDraft('mobileNumber', event.target.value)}
                    placeholder="+27 82 000 0000"
                    type="tel"
                    value={profileDraft.mobileNumber}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-job-title">
                    Job title
                  </label>
                  <input
                    autoComplete="organization-title"
                    className="field"
                    id="profile-job-title"
                    onChange={(event) => updateProfileDraft('jobTitle', event.target.value)}
                    placeholder="Founder"
                    type="text"
                    value={profileDraft.jobTitle}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-location">
                    Location
                  </label>
                  <input
                    autoComplete="address-level2"
                    className="field"
                    id="profile-location"
                    onChange={(event) => updateProfileDraft('location', event.target.value)}
                    placeholder="Johannesburg, South Africa"
                    type="text"
                    value={profileDraft.location}
                  />
                </div>
              </div>
              <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700" htmlFor="profile-website">
                Website
              </label>
              <input
                autoComplete="url"
                className="field"
                id="profile-website"
                onChange={(event) => updateProfileDraft('website', event.target.value)}
                placeholder="https://example.com"
                type="url"
                value={profileDraft.website}
              />
              <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700" htmlFor="profile-bio">
                Bio
              </label>
              <textarea
                className="field min-h-24"
                id="profile-bio"
                onChange={(event) => updateProfileDraft('bio', event.target.value)}
                placeholder="Short profile description"
                value={profileDraft.bio}
              />
              <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700" htmlFor="profile-photo-url">
                Profile image URL
              </label>
              <input
                className="field"
                id="profile-photo-url"
                onChange={(event) => updateProfileDraft('photoURL', event.target.value)}
                placeholder="https://example.com/profile.jpg"
                type="url"
                value={profileDraft.photoURL}
              />
            </div>
          </section>

          <section className="profile-form-section">
            <p className="label-caps">Organisation details</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-organisation-name">
                  Organisation
                </label>
                <input
                  autoComplete="organization"
                  className="field"
                  id="profile-organisation-name"
                  onChange={(event) => updateProfileDraft('organisationName', event.target.value)}
                  placeholder="Company or team name"
                  type="text"
                  value={profileDraft.organisationName}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-organisation-role">
                  Organisation role
                </label>
                <input
                  className="field"
                  id="profile-organisation-role"
                  onChange={(event) => updateProfileDraft('organisationRole', event.target.value)}
                  placeholder="Owner, admin, member"
                  type="text"
                  value={profileDraft.organisationRole}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-organisation-website">
                  Organisation website
                </label>
                <input
                  className="field"
                  id="profile-organisation-website"
                  onChange={(event) => updateProfileDraft('organisationWebsite', event.target.value)}
                  placeholder="https://company.com"
                  type="url"
                  value={profileDraft.organisationWebsite}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700" htmlFor="profile-organisation-size">
                  Organisation size
                </label>
                <select
                  className="field"
                  id="profile-organisation-size"
                  onChange={(event) => updateProfileDraft('organisationSize', event.target.value)}
                  value={profileDraft.organisationSize}
                >
                  <option value="">Select size</option>
                  <option value="solo">Solo</option>
                  <option value="2-10">2-10</option>
                  <option value="11-50">11-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201+">201+</option>
                </select>
              </div>
            </div>
            <label className="mb-2 mt-4 block text-sm font-semibold text-slate-700" htmlFor="profile-organisation-address">
              Organisation address
            </label>
            <textarea
              className="field min-h-20"
              id="profile-organisation-address"
              onChange={(event) => updateProfileDraft('organisationAddress', event.target.value)}
              placeholder="Business address"
              value={profileDraft.organisationAddress}
            />
          </section>

          <section className="profile-form-section">
            <p className="label-caps">Access</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Active subscription</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">
                  {formatProfileLabel(subscriptionPlan)} - {formatProfileLabel(subscriptionStatus)}
                </p>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Role</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">{formatProfileLabel(profile.role || 'user')}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {featureKeys.length ? featureKeys.map((featureKey) => (
                <span className="chip" key={featureKey}>
                  {availableFeatureLabels[featureKey] || formatProfileLabel(featureKey)}
                </span>
              )) : (
                <p className="text-sm text-slate-500">No additional features are enabled.</p>
              )}
            </div>
          </section>

          {error && <p className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-5">
            <button className="nav-button" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="nav-button active" disabled={isSaving} type="submit">
              {isSaving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function UserAvatar({ user }) {
  const initials = useMemo(() => {
    const source = user.displayName || user.email || 'User';
    return source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  }, [user.displayName, user.email]);

  if (user.photoURL) {
    return <img alt="" className="profile-avatar" src={user.photoURL} />;
  }

  return <span className="profile-avatar bg-slate-950 text-sm font-semibold text-white">{initials}</span>;
}

function AuthPanel({ user }) {
  const [authMode, setAuthMode] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setAuthMode(null);
        setIsMenuOpen(false);
        setIsProfileOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!user) {
    return (
      <>
        <button
          aria-label="Sign in"
          className="nav-button active inline-flex h-10 w-10 items-center justify-center gap-2 px-0 sm:w-auto sm:px-3"
          onClick={() => setAuthMode('signIn')}
          title="Sign in"
          type="button"
        >
          <SignInIcon />
          <span className="hidden sm:inline">Sign in</span>
        </button>
        {authMode && (
          <AuthModal
            mode={authMode}
            onClose={() => setAuthMode(null)}
            onModeChange={setAuthMode}
          />
        )}
      </>
    );
  }

  return (
    <div className="relative flex items-center gap-3">
      <span className="hidden max-w-48 truncate text-sm text-slate-600 sm:inline">{user.displayName || user.email}</span>
      <button
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-label="Open profile menu"
        className="profile-avatar-button"
        onClick={() => setIsMenuOpen((current) => !current)}
        type="button"
      >
        <UserAvatar user={user} />
      </button>

      {isMenuOpen && (
        <div className="profile-menu" role="menu">
          <button
            className="profile-menu-item"
            onClick={() => {
              setIsMenuOpen(false);
              setIsProfileOpen(true);
            }}
            role="menuitem"
            type="button"
          >
            Profile
          </button>
          <button
            className="profile-menu-item text-red-700"
            onClick={() => {
              setIsMenuOpen(false);
              authService.signOut();
            }}
            role="menuitem"
            type="button"
          >
            Sign out
          </button>
        </div>
      )}

      {isProfileOpen && <ProfileModal user={user} onClose={() => setIsProfileOpen(false)} />}
    </div>
  );
}

export default AuthPanel;
